import React, { useEffect } from "react";
import PageForm from "components/pageForm";
import Indicator from "pages/createMission/indicator";
import FooterButton from "pages/createMission/footer.button";
import Button from "components/button";
import CreateMissionSubNav from "components/subNav/createMission.subNav";
import FormsCreate from "pages/createMission/formsCreate";
import UseContext from "hooks/useContext";
import ApiDatabase from "server";
import { toast } from "react-toastify";
import useModal from "../components/modal/useModal";
import { dateToEpoch } from "../helper/helper";

// Wizard step indices preserved for compatibility, but only a subset is
// actually rendered (see formsCreate.js). The mission-specific path is:
// 0 -> Select template (mandatory)
// 3 -> Motif
// 5 -> Schedule
// 7 -> Invitation
// 10 -> Confirmation
// 11 -> End
const VALID_PAGES = [0, 3, 5, 7, 10, 11];
const MISSION_PAGE_ORDER = [3, 5, 7, 10];

const nextPageFrom = (page) => {
    if (page === 0) return 3;
    const idx = VALID_PAGES.indexOf(page);
    return VALID_PAGES[idx + 1] ?? page;
};

const prevPageFrom = (page) => {
    const idx = VALID_PAGES.indexOf(page);
    return VALID_PAGES[Math.max(0, idx - 1)] ?? page;
};

const CreateOfMission = () => {
    const { toggle } = useModal();
    const {
        page,
        setPage,
        data,
        setData,
        companyData,
        isDateValid,
        selectedMissionTemplate,
        setSelectedMissionTemplate,
    } = UseContext();

    const [isSubmitLoading, setIsSubmitLoading] = React.useState(false);

    useEffect(() => {
        if (!VALID_PAGES.includes(page)) {
            setPage(0);
        }
    }, [page, setPage]);

    // Hydrate `data` from the selected template each time the choice changes.
    useEffect(() => {
        if (!selectedMissionTemplate) return;
        const t = selectedMissionTemplate;
        setData((prev) => ({
            ...prev,
            activity: t.activity || "",
            qualification: t.title || t.qualification || "",
            tools: t.tools || [],
            licences: t.licences || [],
            languages: t.language || [],
            contact_name: t.contact?.name || "",
            contact_poste: t.contact?.poste || "",
            location: t.location || "",
            other: t.other || "",
            other_interim: t.other_interim || "",
            salary:
                typeof t.salary?.wage_per_hour === "number"
                    ? String(t.salary.wage_per_hour)
                    : "",
        }));
    }, [selectedMissionTemplate, setData]);

    const handlePrev = () => setPage(prevPageFrom(page));
    const handleNext = () => setPage(nextPageFrom(page));

    const handleSubmit = (e) => {
        setIsSubmitLoading(true);
        e.preventDefault();
        if (!selectedMissionTemplate?._id) {
            setIsSubmitLoading(false);
            toast.error("Veuillez sélectionner une fiche de poste");
            return;
        }
        const idCompany = companyData._id;

        if (data.schedule.length > 0) {
            for (let i = 0; i < data.schedule.length; i++) {
                if (data.schedule[i].days.length > 0) {
                    for (let j = 0; j < data.schedule[i].days.length; j++) {
                        if (data.schedule[i].days[j].date) {
                            data.schedule[i].days[j].date = dateToEpoch(
                                new Date(data.schedule[i].days[j].date)
                            )
                                .toString()
                                .split(" ")
                                .slice(0, 4)
                                .join(" ");
                        }
                    }
                }
            }
        }

        const dataToSend = {
            ...data,
            dateStart:
                dateToEpoch(data.dateStart).getFullYear() +
                "-" +
                ("0" +
                    (dateToEpoch(data.dateStart).getMonth() + 1)
                        .toString()
                        .slice(-2)) +
                "-" +
                ("0" +
                    dateToEpoch(data.dateStart).getDate().toString().slice(-2)),
            dateEnd:
                dateToEpoch(data.dateEnd).getFullYear() +
                "-" +
                ("0" +
                    (dateToEpoch(data.dateEnd).getMonth() + 1)
                        .toString()
                        .slice(-2)) +
                "-" +
                ("0" + dateToEpoch(data.dateEnd).getDate().toString().slice(-2)),
        };

        ApiDatabase.postCreateMission(
            {
                dataToSend,
                idCompany,
                isDesktop: true,
                missionTemplateId: selectedMissionTemplate._id,
            },
            () => {
                setData({
                    activity: "",
                    qualification: "",
                    tools: [],
                    licences: [],
                    languages: [],
                    contact_name: "",
                    contact_poste: "",
                    location: "",
                    dateStart: "",
                    dateEnd: "",
                    schedule: [],
                    isScheduleCheck: true,
                    isConfirmedSchedule: false,
                    isScheduleRequired: false,
                    users_invite: [],
                    motif: "",
                    other: "",
                    other_interim: "",
                    salary: "",
                });
                setSelectedMissionTemplate(null);
                handleNext();
                setIsSubmitLoading(false);
            },
            (err) => {
                console.log(err);
                setIsSubmitLoading(false);
                toast.error(
                    err?.response?.data?.error || "Création impossible"
                );
            }
        );
    };

    const conditionMotif = (motifData) => {
        switch (motifData?.type) {
            case "1":
                return motifData.message !== "";
            case "2":
                return (
                    motifData.date !== "" &&
                    motifData.name !== "" &&
                    motifData.prenom !== ""
                );
            case "3":
                return (
                    motifData.message !== "" &&
                    motifData.name !== "" &&
                    motifData.prenom !== "" &&
                    motifData.poste !== ""
                );
            case "4":
                return motifData.message !== "";
            case "5":
                return motifData.message !== "";
            default:
                return false;
        }
    };

    const handlenxt = () => {
        switch (page) {
            case 0:
                if (!selectedMissionTemplate) {
                    toast.error("Veuillez sélectionner une fiche de poste");
                    return;
                }
                if (selectedMissionTemplate.isBelowMinimumWage) {
                    toast.error(
                        "Cette fiche est inférieure au SMIC, mettez-la à jour avant utilisation"
                    );
                    return;
                }
                handleNext();
                break;
            case 3:
                if (data.motif === "") {
                    toast.error("Veuillez sélectionner un motif");
                    return;
                }
                if (!conditionMotif(data?.motif)) {
                    toast.error(
                        "Veuillez remplir les champs du motif sélectionné"
                    );
                    return;
                }
                handleNext();
                break;
            case 5:
                if (
                    !data.dateStart ||
                    !data.dateEnd ||
                    !isDateValid
                ) {
                    toast.error(
                        "Veuillez renseigner les dates de la mission avec une période valide"
                    );
                    return;
                }
                if (data.isScheduleCheck) {
                    if (data.schedule.length > 0) {
                        let isValidSchedule = false;
                        let isScheduleNotComplete = false;
                        for (let i = 0; i < data.schedule.length; i++) {
                            for (
                                let j = 0;
                                j < data.schedule[i]?.days?.length;
                                j++
                            ) {
                                if (data.schedule[i].days[j].periods) {
                                    if (
                                        data.schedule[i].days[j].periods
                                            .periode_one_start !== "" &&
                                        data.schedule[i].days[j].periods
                                            .periode_one_end !== ""
                                    ) {
                                        isValidSchedule = true;
                                    } else {
                                        isScheduleNotComplete = true;
                                    }
                                } else {
                                    isScheduleNotComplete = true;
                                }
                            }
                        }
                        if (isValidSchedule) {
                            if (!isScheduleNotComplete) {
                                handleNext();
                            } else if (data.isConfirmedSchedule) {
                                handleNext();
                            } else {
                                toggle("cM_confirmSchedule");
                            }
                        } else if (data.isScheduleRequired) {
                            toast.error("Veuillez remplir le planning");
                        } else {
                            toast.error(
                                "Veuillez remplir le planning ou retirer l'ajout d'un planning à la mission"
                            );
                        }
                    } else if (data.isScheduleRequired) {
                        toast.error("Veuillez remplir le planning");
                    } else {
                        toast.error(
                            "Veuillez remplir le planning ou retirer l'ajout d'un planning à la mission"
                        );
                    }
                } else {
                    handleNext();
                }
                break;
            case 7:
                handleNext();
                break;
            case 10:
                handleNext();
                break;
            default:
                break;
        }
    };

    const showFooter = page !== 11;
    const showBack =
        MISSION_PAGE_ORDER.indexOf(page) > 0 || (page === 0 ? false : true);
    const stepIndex =
        page === 0 ? 0 : MISSION_PAGE_ORDER.indexOf(page) + 1;

    return (
        <>
            <PageForm>
                <div className="col-span-12 bg-white sticky z-10 top-16">
                    <CreateMissionSubNav />
                </div>
                <div className="col-span-full pb-24">
                    <div className="w-full mx-auto lg:w-[800px] px-6 py-8">
                        <FormsCreate />
                    </div>
                </div>

                {showFooter && (
                    <div className="col-span-full">
                        <div className="fixed bottom-0 left-[80px] sidebar-expanded:left-64 2xl:left-64 right-0 bg-white border-t border-gray-100 z-20">
                            <div className="w-full mx-auto lg:w-[800px] px-6 py-4">
                                <div className="flex justify-between items-center gap-4">
                                    <Indicator formIndex={stepIndex} />
                                    <div className="flex gap-3 shrink-0">
                                        {showBack && page !== 0 ? (
                                            <Button
                                                size={"LG"}
                                                color={"SECONDARY"}
                                                onClick={handlePrev}
                                            >
                                                Retour
                                            </Button>
                                        ) : null}
                                        <FooterButton
                                            oC1={handlenxt}
                                            oC2={handleSubmit}
                                            isLoading={isSubmitLoading}
                                            disabled={
                                                page === 0 &&
                                                (!selectedMissionTemplate ||
                                                    selectedMissionTemplate.isBelowMinimumWage)
                                            }
                                            templateSelectionMode={page === 0}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </PageForm>
        </>
    );
};

export default CreateOfMission;
